import axiosClient from './axiosClient';

/**
 * The first-party funnel.
 *
 * Product code should not call this directly — go through `trackFunnel` in
 * `lib/conversionTracking.ts`, which also sends the vendor copy and is the one
 * place the two vocabularies are mapped.
 */

/** Mirrors `FUNNEL_EVENT_TYPES` in backend/src/models/Conversion.ts. */
export type FunnelEventType =
  | 'landing_page_view'
  | 'lead_form_start'
  | 'lead_form_submit'
  | 'lead_created'
  | 'lead_magnet_download'
  | 'nurture_click'
  /**
   * Server-written, at the moment an account row exists. Listed here so this
   * union stays a mirror of the server's vocabulary — never to be posted from
   * the browser, which the endpoint refuses anyway. Meta's own
   * `complete_registration` still fires from `pages/Auth.tsx` and is a separate
   * thing: that copy is the ad platform's, this one is ours.
   */
  | 'account_created'
  | 'sales_page_view'
  | 'whatsapp_contact_click'
  | 'telegram_contact_click'
  | 'syllabus_expand'
  | 'preview_interaction'
  | 'enrol_click'
  | 'cart_add'
  | 'checkout_start'
  | 'promo_code_apply'
  | 'payment_initiated'
  | 'payment_confirmed'
  | 'payment_failed'
  | 'enrolment_granted'
  | 'first_lesson_started';

export interface FunnelEventInput {
  type: FunnelEventType;
  courseSlug?: string;
  anonId?: string;
  attribution?: Record<string, string>;
  promoCode?: string;
}

/**
 * Records one step. Never rejects.
 *
 * A measurement that can throw is a measurement that can break a checkout, and
 * this one is called from click handlers on the page that takes people's money.
 * The endpoint answers 204 to everything for the same reason.
 */
export const postFunnelEvent = async (input: FunnelEventInput): Promise<void> => {
  try {
    await axiosClient.post('/conversion/track', input);
  } catch {
    // Deliberately silent. Nothing on the page depends on this landing, and a
    // console error on every blocked request would be noise, not signal.
  }
};

export interface CourseFunnelRow {
  courseSlug: string;
  courseTitle: string;
  salesPageViews: number;
  enrolClicks: number;
  cartAdds: number;
  checkoutStarts: number;
  paymentsConfirmed: number;
  enrolments: number;
  firstLessonStarts: number;
  revenue: number;
  /** Null where the denominator is zero — render "No data yet", never "0%". */
  viewToEnrolClick: number | null;
  enrolClickToCheckout: number | null;
  checkoutToPayment: number | null;
  viewToPayment: number | null;
  paymentToActivation: number | null;
}

export interface ConversionDashboard {
  range: { from: string | null; to: string | null };
  courses: CourseFunnelRow[];
  checkouts: Record<string, { count: number; amount: number }>;
  /**
   * Accounts created in the window. Deliberately outside `courses` and
   * deliberately not narrowed by the course filter: registration happens on
   * `/auth`, which belongs to no course, so a per-course figure would be zero
   * for every course and would read as a conversion failure rather than as the
   * wrong question.
   */
  registrations: number;
  facets: {
    sources: string[];
    mediums: string[];
    campaigns: string[];
    leadMagnets: string[];
  };
}

export interface ConversionFilters {
  from?: string;
  to?: string;
  courseSlug?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  leadMagnet?: string;
}

/** The admin conversion view. Requires `conversion:read`. */
export const getConversionDashboard = async (
  filters: ConversionFilters = {}
): Promise<ConversionDashboard> => {
  const response = await axiosClient.get('/conversion/dashboard', { params: filters });
  return response.data;
};
